import { useState, useEffect, useRef } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import productService from '../../services/productService';
import brandService from '../../services/brandService';
import categoryService from '../../services/categoryService';
import productTypeService from '../../services/productTypeService';
import occasionService from '../../services/occasionService';
import seasonService from '../../services/seasonService';
import concentrationService from '../../services/concentrationService';
import fragranceNoteService from '../../services/fragranceNoteService';
import imageService from '../../services/imageService';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';
import { getProductImageUrl } from '../../utils/formatters';

const ProductModal = ({ isOpen, onClose, onSuccess, productId = null }) => {
  const { get, isRtl } = useLocalization();
  const isEditMode = productId !== null;
  const [formData, setFormData] = useState({
    nameEn: '',
    nameAr: '',
    descriptionEn: '',
    descriptionAr: '',
    brandId: '',
    categoryId: '',
    productTypeId: '',
    color: '',
    currencyCode: 'USD',
    classification: 0,
    kind: 0,
    isNew: false,
    occasionIds: [],
    seasonIds: [],
    variants: [],
    productImages: [],
    notes: [],
  });
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [concentrations, setConcentrations] = useState([]);
  const [fragranceNotes, setFragranceNotes] = useState([]);
  const [topNotes, setTopNotes] = useState([]);
  const [middleNotes, setMiddleNotes] = useState([]);
  const [baseNotes, setBaseNotes] = useState([]);
  const [topNoteSearch, setTopNoteSearch] = useState('');
  const [middleNoteSearch, setMiddleNoteSearch] = useState('');
  const [baseNoteSearch, setBaseNoteSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [deletedVariantIds, setDeletedVariantIds] = useState([]);
  const [deletedProductImageIds, setDeletedProductImageIds] = useState([]);
  const totalSteps = 4;
  const variantFileInputRefs = useRef({});
  const productImageInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadOptions();
      setTopNoteSearch('');
      setMiddleNoteSearch('');
      setBaseNoteSearch('');
      if (isEditMode && productId) {
        loadProduct();
      } else {
        resetForm();
      }
    }
  }, [isOpen, productId]);

  const resetForm = () => {
    setFormData({
      nameEn: '',
      nameAr: '',
      descriptionEn: '',
      descriptionAr: '',
      brandId: '',
      categoryId: '',
      productTypeId: '',
      color: '',
      currencyCode: 'USD',
      classification: 0,
      kind: 0,
      isNew: false,
      occasionIds: [],
      seasonIds: [],
      variants: [],
      productImages: [],
      notes: [],
    });
    setTopNotes([]);
    setMiddleNotes([]);
    setBaseNotes([]);
    setTopNoteSearch('');
    setMiddleNoteSearch('');
    setBaseNoteSearch('');
    setDeletedVariantIds([]);
    setDeletedProductImageIds([]);
    setCurrentStep(1);
  };

  const loadProduct = async () => {
    if (!productId) return;

    setIsLoadingProduct(true);
    try {
      const product = await productService.getDetails(productId);
      const allNotes = await fragranceNoteService.getAll();

      setFormData({
        nameEn: product.nameEn || '',
        nameAr: product.nameAr || '',
        descriptionEn: product.descriptionEn || '',
        descriptionAr: product.descriptionAr || '',
        brandId: product.brandId || '',
        categoryId: product.categoryId || '',
        productTypeId: product.productTypeId || '',
        color: product.color || '',
        currencyCode: product.currencyCode || 'USD',
        classification: product.classification || 0,
        kind: product.kind || 0,
        isNew: product.isNew === true,
        occasionIds: product.occasions?.map(o => o.id) || [],
        seasonIds: product.seasons?.map(s => s.id) || [],
        variants: product.variants?.map(v => ({
          id: v.id,
          sizeLabelEn: v.sizeLabelEn || '',
          sizeLabelAr: v.sizeLabelAr || '',
          price: v.price || '',
          inStock: v.inStock !== false,
          sku: v.sku || '',
          concentrationId: v.concentrationId || null,
          isDisabled: v.isDisabled || false,
          images: (v.images || []).map(img => {
            let relativePath = img.url || '';
            let fullUrl = img.url || '';
            if (fullUrl.includes('/media/')) {
              relativePath = fullUrl.split('/media/')[1];
            } else if (!fullUrl.startsWith('http')) {
              fullUrl = getProductImageUrl(relativePath) || fullUrl;
            }
            return {
              id: img.id,
              url: relativePath,
              dataUrl: fullUrl,
              isPrimary: img.isPrimary || false,
              displayOrder: img.displayOrder || 0,
            };
          }),
          deleteImageIds: [],
        })) || [],
        productImages: product.productImages?.map(img => {
          let relativePath = img.url || '';
          let fullUrl = img.url || '';
          if (fullUrl.includes('/media/')) {
            relativePath = fullUrl.split('/media/')[1];
          } else if (!fullUrl.startsWith('http')) {
            fullUrl = getProductImageUrl(relativePath) || fullUrl;
          }
          return {
            id: img.id,
            url: relativePath,
            dataUrl: fullUrl,
            isPrimary: img.isPrimary || false,
            displayOrder: img.displayOrder || 0,
          };
        }) || [],
        notes: [],
      });

      const notesById = new Map((allNotes || []).map((note) => [String(note.id), note]));
      const getTierValue = (tier) => {
        if (typeof tier === 'number') return tier;
        const normalizedTier = String(tier || '').toLowerCase().trim();
        if (normalizedTier === 'top' || normalizedTier === '1') return 1;
        if (normalizedTier === 'middle' || normalizedTier === 'heart' || normalizedTier === '2') return 2;
        if (normalizedTier === 'base' || normalizedTier === '3') return 3;
        return Number(normalizedTier) || 0;
      };

      const topNotesList = (product.notes || [])
        .filter((n) => getTierValue(n.tier) === 1)
        .map((n) => notesById.get(String(n.fragranceNoteId)))
        .filter(Boolean);
      const middleNotesList = (product.notes || [])
        .filter((n) => getTierValue(n.tier) === 2)
        .map((n) => notesById.get(String(n.fragranceNoteId)))
        .filter(Boolean);
      const baseNotesList = (product.notes || [])
        .filter((n) => getTierValue(n.tier) === 3)
        .map((n) => notesById.get(String(n.fragranceNoteId)))
        .filter(Boolean);

      setTopNotes(topNotesList);
      setMiddleNotes(middleNotesList);
      setBaseNotes(baseNotesList);
    } catch (error) {
      console.error('Error loading product:', error);
      alert(isRtl ? 'حدث خطأ أثناء تحميل المنتج' : 'Error loading product');
    } finally {
      setIsLoadingProduct(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [
        brandsData,
        categoriesData,
        typesData,
        occasionsData,
        seasonsData,
        concentrationsData,
        notesData,
      ] = await Promise.all([
        brandService.getAll(),
        categoryService.getAll(),
        productTypeService.getAll(),
        occasionService.getAll(),
        seasonService.getAll(),
        concentrationService.getAll(),
        fragranceNoteService.getAll(),
      ]);
      setBrands(brandsData);
      setCategories(categoriesData);
      setProductTypes(typesData);
      setOccasions(occasionsData);
      setSeasons(seasonsData);
      setConcentrations(concentrationsData);
      setFragranceNotes(notesData);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const isPerfumeProduct = () => {
    const selectedType = productTypes.find((t) => t.id === formData.productTypeId);
    return selectedType?.nameEn?.toLowerCase() === 'perfume';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentStep === 1) {
      if (!formData.nameEn || !formData.nameAr || !formData.brandId || !formData.categoryId || !formData.productTypeId) {
        alert(isRtl ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
        return;
      }
    }

    if (currentStep === 2) {
      if (formData.variants.length === 0) {
        alert(isRtl ? 'يرجى إضافة متغير واحد على الأقل' : 'Please add at least one variant');
        return;
      }

      for (let i = 0; i < formData.variants.length; i++) {
        const variant = formData.variants[i];
        if (!variant.sizeLabelEn || !variant.sizeLabelAr || !variant.price) {
          alert(isRtl ? `يرجى ملء جميع الحقول المطلوبة للمتغير ${i + 1}` : `Please fill all required fields for variant ${i + 1}`);
          return;
        }
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      return;
    }

    setIsLoading(true);
    try {
      const uploadNewImages = async (images) => {
        const newImageFiles = images.filter(img => !img.id && img.file).map(img => img.file);
        if (newImageFiles.length === 0) return images;

        try {
          const uploadResult = await imageService.uploadImages(newImageFiles, 'products');
          if (uploadResult.success && uploadResult.relativePaths) {
            let uploadedIndex = 0;
            return images.map(img => {
              if (!img.id && img.file) {
                const relativePath = uploadResult.relativePaths[uploadedIndex];
                uploadedIndex++;
                return {
                  ...img,
                  url: relativePath,
                  dataUrl: uploadResult.imageUrls?.[uploadedIndex - 1] || img.dataUrl,
                };
              }
              return img;
            });
          }
        } catch (error) {
          console.error('Error uploading images:', error);
          const msg = error?.message || (isRtl ? 'فشل رفع الصور' : 'Failed to upload images');
          throw new Error(msg);
        }
        return images;
      };

      const processedVariants = await Promise.all(
        formData.variants.map(async (v) => {
          const processedImages = await uploadNewImages(v.images || []);
          return { ...v, images: processedImages };
        })
      );

      const processedProductImages = await uploadNewImages(formData.productImages || []);

      const allNotes = [
        ...topNotes.map((n) => ({ fragranceNoteId: n.id, tier: 1 })),
        ...middleNotes.map((n) => ({ fragranceNoteId: n.id, tier: 2 })),
        ...baseNotes.map((n) => ({ fragranceNoteId: n.id, tier: 3 })),
      ];

      if (isEditMode) {
        const requestData = {
          nameEn: formData.nameEn,
          nameAr: formData.nameAr,
          brandId: formData.brandId,
          categoryId: formData.categoryId,
          productTypeId: formData.productTypeId,
          currencyCode: formData.currencyCode,
          color: formData.color || null,
          classification: formData.classification,
          kind: formData.kind,
          isNew: formData.isNew === true,
          descriptionEn: formData.descriptionEn || null,
          descriptionAr: formData.descriptionAr || null,
          occasionIds: formData.occasionIds,
          seasonIds: formData.seasonIds,
          variants: processedVariants.map((v) => ({
            id: v.id || null,
            sizeLabelEn: v.sizeLabelEn,
            sizeLabelAr: v.sizeLabelAr,
            price: parseFloat(v.price) || 0,
            inStock: v.inStock !== false,
            sku: v.sku || null,
            concentrationId: v.concentrationId || null,
            isDisabled: v.isDisabled || false,
            images: (v.images || []).map((img, imgIndex) => {
              let fileName = img.url || '';
              if (fileName.includes('/media/')) {
                fileName = fileName.split('/media/')[1];
              }
              return {
                id: img.id || null,
                fileName: fileName,
                displayOrder: imgIndex,
                isPrimary: img.isPrimary || false,
              };
            }),
            deleteVariantImageIds: v.deleteImageIds || [],
          })),
          deleteVariantIds: deletedVariantIds,
          productImages: processedProductImages.map((img, index) => {
            let fileName = img.url || '';
            if (fileName.includes('/media/')) {
              fileName = fileName.split('/media/')[1];
            }
            return {
              id: img.id || null,
              fileName: fileName,
              displayOrder: index,
              isPrimary: img.isPrimary || false,
            };
          }),
          deleteProductImageIds: deletedProductImageIds,
          notes: allNotes,
        };

        await productService.update(productId, requestData);
      } else {
        const requestData = {
          nameEn: formData.nameEn,
          nameAr: formData.nameAr,
          brandId: formData.brandId,
          categoryId: formData.categoryId,
          productTypeId: formData.productTypeId,
          currencyCode: formData.currencyCode,
          color: formData.color || null,
          classification: formData.classification,
          kind: formData.kind,
          isNew: formData.isNew === true,
          descriptionEn: formData.descriptionEn || null,
          descriptionAr: formData.descriptionAr || null,
          occasionIds: formData.occasionIds,
          seasonIds: formData.seasonIds,
          variants: processedVariants.map((v) => ({
            sizeLabelEn: v.sizeLabelEn,
            sizeLabelAr: v.sizeLabelAr,
            price: parseFloat(v.price) || 0,
            inStock: v.inStock !== false,
            sku: v.sku || null,
            concentrationId: v.concentrationId || null,
            isDisabled: v.isDisabled || false,
            images: (v.images || []).map((img, imgIndex) => {
              let url = img.url || '';
              if (url.includes('/media/')) {
                url = url.split('/media/')[1];
              }
              return {
                url: url,
                displayOrder: imgIndex,
                isPrimary: img.isPrimary || false,
              };
            }),
          })),
          productImages: processedProductImages.map((img, index) => {
            let url = img.url || '';
            if (url.includes('/media/')) {
              url = url.split('/media/')[1];
            }
            return {
              url: url,
              displayOrder: index,
              isPrimary: img.isPrimary || false,
            };
          }),
          notes: allNotes,
        };

        await productService.create(requestData);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} product:`, error);
      alert(isRtl ? `حدث خطأ أثناء ${isEditMode ? 'تحديث' : 'إنشاء'} المنتج` : `Error ${isEditMode ? 'updating' : 'creating'} product`);
    } finally {
      setIsLoading(false);
    }
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        {
          sizeLabelEn: '',
          sizeLabelAr: '',
          price: '',
          inStock: true,
          sku: '',
          concentrationId: null,
          isDisabled: false,
          images: [],
          deleteImageIds: [],
        },
      ],
    });
  };

  const removeVariant = (index) => {
    const variant = formData.variants[index];
    if (variant.id) {
      setDeletedVariantIds([...deletedVariantIds, variant.id]);
    }
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index),
    });
  };

  const updateVariant = (index, field, value) => {
    const updatedVariants = [...formData.variants];
    updatedVariants[index] = { ...updatedVariants[index], [field]: value };
    setFormData({ ...formData, variants: updatedVariants });
  };

  const handleVariantImageUpload = (index, files) => {
    const updatedVariants = [...formData.variants];
    const variant = updatedVariants[index];
    const newImages = [];

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push({
          dataUrl: e.target.result,
          file: file,
          isPrimary: variant.images.length === 0 && newImages.length === 0,
        });

        if (newImages.length === files.length) {
          variant.images = [...(variant.images || []), ...newImages];
          updatedVariants[index] = variant;
          setFormData({ ...formData, variants: updatedVariants });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeVariantImage = (variantIndex, imageIndex) => {
    const updatedVariants = [...formData.variants];
    const image = updatedVariants[variantIndex].images[imageIndex];

    if (image.id) {
      if (!updatedVariants[variantIndex].deleteImageIds) {
        updatedVariants[variantIndex].deleteImageIds = [];
      }
      updatedVariants[variantIndex].deleteImageIds.push(image.id);
    }

    updatedVariants[variantIndex].images = updatedVariants[variantIndex].images.filter((_, i) => i !== imageIndex);
    setFormData({ ...formData, variants: updatedVariants });
  };

  const setVariantImagePrimary = (variantIndex, imageIndex) => {
    const updatedVariants = [...formData.variants];
    updatedVariants[variantIndex].images = updatedVariants[variantIndex].images.map((img, i) => ({
      ...img,
      isPrimary: i === imageIndex,
    }));
    setFormData({ ...formData, variants: updatedVariants });
  };

  const toggleOccasion = (occasionId) => {
    const occasionIds = formData.occasionIds.includes(occasionId)
      ? formData.occasionIds.filter((id) => id !== occasionId)
      : [...formData.occasionIds, occasionId];
    setFormData({ ...formData, occasionIds });
  };

  const toggleSeason = (seasonId) => {
    const seasonIds = formData.seasonIds.includes(seasonId)
      ? formData.seasonIds.filter((id) => id !== seasonId)
      : [...formData.seasonIds, seasonId];
    setFormData({ ...formData, seasonIds });
  };

  const handleProductImageUpload = (files) => {
    const newImages = [];
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push({
          dataUrl: e.target.result,
          file: file,
          isPrimary: formData.productImages.length === 0 && newImages.length === 0,
        });

        if (newImages.length === files.length) {
          setFormData({
            ...formData,
            productImages: [...formData.productImages, ...newImages],
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeProductImage = (index) => {
    const image = formData.productImages[index];

    if (image.id) {
      setDeletedProductImageIds([...deletedProductImageIds, image.id]);
    }

    setFormData({
      ...formData,
      productImages: formData.productImages.filter((_, i) => i !== index),
    });
  };

  const setProductImagePrimary = (index) => {
    setFormData({
      ...formData,
      productImages: formData.productImages.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      })),
    });
  };

  const addFragranceNote = (note, tier) => {
    const noteObj = fragranceNotes.find((n) => n.id === note);
    if (!noteObj) return;

    if (tier === 1) {
      if (!topNotes.find((n) => n.id === note)) {
        setTopNotes([...topNotes, noteObj]);
      }
    } else if (tier === 2) {
      if (!middleNotes.find((n) => n.id === note)) {
        setMiddleNotes([...middleNotes, noteObj]);
      }
    } else if (tier === 3) {
      if (!baseNotes.find((n) => n.id === note)) {
        setBaseNotes([...baseNotes, noteObj]);
      }
    }
  };

  const normalizeNoteSearch = (value) => (value || '').toString().toLowerCase().trim();
  const normalizeNoteType = (value) => {
    const raw = normalizeNoteSearch(value);
    if (!raw) return '';
    if (['1', 'top', 'upper', 'head'].includes(raw)) return 'top';
    if (['2', 'middle', 'mid', 'heart'].includes(raw)) return 'middle';
    if (['3', 'base', 'bottom', 'drydown'].includes(raw)) return 'base';
    if (raw.includes('top') || raw.includes('upper') || raw.includes('head') || raw.includes('علو')) return 'top';
    if (raw.includes('mid') || raw.includes('middle') || raw.includes('heart') || raw.includes('وس')) return 'middle';
    if (raw.includes('base') || raw.includes('bottom') || raw.includes('قاعد')) return 'base';
    return raw;
  };
  const filterNotes = (notes, selectedNotes, searchTerm, type) => {
    const query = normalizeNoteSearch(searchTerm);
    const hasTypedNotes = notes.some((note) => normalizeNoteType(note.type));
    return notes
      .filter((note) => {
        if (!type || !hasTypedNotes) return true;
        const noteType = normalizeNoteType(note.type);
        return noteType === type;
      })
      .filter((note) => !selectedNotes.find((selected) => selected.id === note.id))
      .filter((note) => {
        if (!query) return true;
        const nameEn = normalizeNoteSearch(note.nameEn);
        const nameAr = normalizeNoteSearch(note.nameAr);
        return nameEn.includes(query) || nameAr.includes(query);
      });
  };

  const getNoteLabel = (note) => {
    const nameAr = note?.nameAr || '';
    const nameEn = note?.nameEn || '';
    if (nameAr && nameEn) return `${nameAr} - ${nameEn}`;
    return nameAr || nameEn || '';
  };

  const removeFragranceNote = (noteId, tier) => {
    if (tier === 1) {
      setTopNotes(topNotes.filter((n) => n.id !== noteId));
    } else if (tier === 2) {
      setMiddleNotes(middleNotes.filter((n) => n.id !== noteId));
    } else if (tier === 3) {
      setBaseNotes(baseNotes.filter((n) => n.id !== noteId));
    }
  };

  const getStepStatus = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'active';
    return 'pending';
  };

  if (isLoadingProduct) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={isRtl ? 'تحميل المنتج...' : 'Loading Product...'} size="xl">
        <div className="modal-loading">
          <div className="modal-loading-spinner"></div>
          <span className="modal-loading-text">
            {isRtl ? 'جاري تحميل بيانات المنتج...' : 'Loading product data...'}
          </span>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? (isRtl ? 'تحديث المنتج' : 'Update Product') : get('add_new_product')}
      size="xl"
      footer={
        <>
          {currentStep > 1 && (
            <Button variant="secondary" onClick={() => setCurrentStep(currentStep - 1)}>
              {isRtl ? 'السابق' : 'Previous'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {get('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {currentStep < totalSteps
              ? isRtl ? 'التالي' : 'Next'
              : isLoading
              ? isRtl ? 'جاري الحفظ...' : 'Saving...'
              : get('save')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {/* Step Wizard Indicator */}
        <div className="wizard-steps">
          {[1, 2, 3, 4].map((step, index) => (
            <div key={step} className="d-flex align-items-center">
              {index > 0 && (
                <div className={`wizard-step-connector ${getStepStatus(step) === 'completed' ? 'completed' : ''}`}></div>
              )}
              <div className={`wizard-step ${getStepStatus(step)}`}>
                {getStepStatus(step) === 'completed' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  step
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="form-section">
            <h3 className="step-title">
              {isRtl ? 'المعلومات الأساسية' : 'Basic Information'}
            </h3>

            <div className="form-row">
              <Input
                label={isRtl ? 'الاسم (إنجليزي) *' : 'Name (English) *'}
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                required
              />
              <Input
                label={isRtl ? 'الاسم (عربي) *' : 'Name (Arabic) *'}
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                required
                dir="rtl"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{isRtl ? 'نوع المنتج *' : 'Product Type *'}</label>
                <select
                  className="form-select"
                  value={formData.productTypeId}
                  onChange={(e) => setFormData({ ...formData, productTypeId: e.target.value })}
                  required
                >
                  <option value="">{isRtl ? 'اختر نوع المنتج' : 'Select Product Type'}</option>
                  {productTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {isRtl ? type.nameAr : type.nameEn}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{isRtl ? 'الفئة *' : 'Category *'}</label>
                <select
                  className="form-select"
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  required
                >
                  <option value="">{isRtl ? 'اختر الفئة' : 'Select Category'}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {isRtl ? category.nameAr : category.nameEn}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{isRtl ? 'العلامة التجارية *' : 'Brand *'}</label>
                <select
                  className="form-select"
                  value={formData.brandId}
                  onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
                  required
                >
                  <option value="">{isRtl ? 'اختر العلامة التجارية' : 'Select Brand'}</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {isRtl ? brand.nameAr : brand.nameEn}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{isRtl ? 'العملة *' : 'Currency *'}</label>
                <select
                  className="form-select"
                  value={formData.currencyCode}
                  onChange={(e) => setFormData({ ...formData, currencyCode: e.target.value })}
                  required
                >
                  <option value="USD">USD (US Dollar)</option>
                </select>
              </div>
            </div>

            {!isPerfumeProduct() && (
              <Input
                label={isRtl ? 'اللون' : 'Color'}
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                placeholder={isRtl ? 'مثال: أسود، ذهبي، فضي' : 'e.g., Black, Gold, Silver'}
              />
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{isRtl ? 'التصنيف *' : 'Classification *'}</label>
                <select
                  className="form-select"
                  value={formData.classification}
                  onChange={(e) => setFormData({ ...formData, classification: parseInt(e.target.value) })}
                  required
                >
                  <option value="0">Designer (تجاري)</option>
                  <option value="1">Niche (متخصص)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{isRtl ? 'نوع المنتج *' : 'Product Kind *'}</label>
                <select
                  className="form-select"
                  value={formData.kind}
                  onChange={(e) => setFormData({ ...formData, kind: parseInt(e.target.value) })}
                  required
                >
                  <option value="0">Standard (عادي)</option>
                  <option value="1">Set (طقم)</option>
                  <option value="2">Gift Set (طقم هدية)</option>
                  <option value="3">Tester (تستر)</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label form-label-inline">
                <input
                  type="checkbox"
                  checked={formData.isNew === true}
                  onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })}
                />
                <span>{isRtl ? 'منتج جديد (يظهر أولاً في الصفحة الرئيسية)' : 'New product (shows first on home page)'}</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">{isRtl ? 'الوصف (إنجليزي)' : 'Description (English)'}</label>
              <textarea
                className="form-textarea"
                value={formData.descriptionEn}
                onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{isRtl ? 'الوصف (عربي)' : 'Description (Arabic)'}</label>
              <textarea
                className="form-textarea"
                value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                rows="3"
                dir="rtl"
              />
            </div>
          </div>
        )}

        {/* Step 2: Variants */}
        {currentStep === 2 && (
          <div className="form-section">
            <h3 className="step-title">{isRtl ? 'المتغيرات' : 'Variants'}</h3>

            <div className="mb-4">
              <button type="button" className="add-item-btn" onClick={addVariant}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {isRtl ? 'إضافة متغير' : 'Add Variant'}
              </button>
            </div>

            {formData.variants.length === 0 ? (
              <div className="modal-empty-state">
                <svg className="modal-empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                </svg>
                <p className="modal-empty-text">
                  {isRtl ? 'لا توجد متغيرات. يرجى إضافة متغير واحد على الأقل.' : 'No variants. Please add at least one variant.'}
                </p>
              </div>
            ) : (
              formData.variants.map((variant, index) => (
                <div key={variant.id || index} className="variant-card">
                  <div className="variant-card-header">
                    <div className="variant-card-title">
                      {isRtl ? `متغير ${index + 1}` : `Variant ${index + 1}`}
                      {variant.id && <span className="variant-card-badge">{isRtl ? 'موجود' : 'Existing'}</span>}
                    </div>
                    <Button variant="secondary" size="small" onClick={() => removeVariant(index)} type="button">
                      {isRtl ? 'حذف' : 'Remove'}
                    </Button>
                  </div>

                  <div className="form-row">
                    <Input
                      label={isRtl ? 'الحجم (إنجليزي) *' : 'Size (English) *'}
                      value={variant.sizeLabelEn}
                      onChange={(e) => updateVariant(index, 'sizeLabelEn', e.target.value)}
                      required
                    />
                    <Input
                      label={isRtl ? 'الحجم (عربي) *' : 'Size (Arabic) *'}
                      value={variant.sizeLabelAr}
                      onChange={(e) => updateVariant(index, 'sizeLabelAr', e.target.value)}
                      required
                      dir="rtl"
                    />
                  </div>

                  <div className="form-row">
                    <Input
                      label={isRtl ? 'السعر (USD) *' : 'Price (USD) *'}
                      type="number"
                      step="0.01"
                      value={variant.price}
                      onChange={(e) => updateVariant(index, 'price', e.target.value)}
                      required
                    />
                    <Input
                      label="SKU"
                      value={variant.sku || ''}
                      onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                    />
                  </div>

                  {isPerfumeProduct() && (
                    <div className="form-group">
                      <label className="form-label">{isRtl ? 'التركيز *' : 'Concentration *'}</label>
                      <select
                        className="form-select"
                        value={variant.concentrationId || ''}
                        onChange={(e) => updateVariant(index, 'concentrationId', e.target.value || null)}
                        required
                      >
                        <option value="">{isRtl ? 'اختر التركيز' : 'Select Concentration'}</option>
                        {concentrations.map((conc) => (
                          <option key={conc.id} value={conc.id}>
                            {isRtl ? conc.nameAr : conc.nameEn} ({conc.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="d-flex gap-4 mb-4">
                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={variant.inStock !== false}
                        onChange={(e) => updateVariant(index, 'inStock', e.target.checked)}
                      />
                      <span className="form-checkbox-label">{isRtl ? 'متوفر في المخزون' : 'In Stock'}</span>
                    </label>

                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={variant.isDisabled || false}
                        onChange={(e) => updateVariant(index, 'isDisabled', e.target.checked)}
                      />
                      <span className="form-checkbox-label">{isRtl ? 'معطل' : 'Disabled'}</span>
                    </label>
                  </div>

                  {/* Variant Images */}
                  <div className="form-group">
                    <label className="form-label">{isRtl ? 'صور المتغير' : 'Variant Images'}</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      ref={(el) => (variantFileInputRefs.current[index] = el)}
                      onChange={(e) => handleVariantImageUpload(index, e.target.files)}
                      hidden
                    />
                    <Button variant="secondary" type="button" size="small" onClick={() => variantFileInputRefs.current[index]?.click()}>
                      {isRtl ? 'رفع الصور' : 'Upload Images'}
                    </Button>

                    {variant.images && variant.images.length > 0 && (
                      <div className="image-grid">
                        {variant.images.map((img, imgIndex) => (
                          <div key={img.id || imgIndex} className={`image-item ${img.isPrimary ? 'primary' : ''}`}>
                            <img src={img.dataUrl || img.url} alt={`Variant ${index + 1} Image ${imgIndex + 1}`} />
                            {img.isPrimary && (
                              <span className="image-item-badge">{isRtl ? 'رئيسي' : 'Primary'}</span>
                            )}
                            <div className="image-item-actions">
                              <button
                                type="button"
                                className="image-item-btn primary-btn"
                                onClick={() => setVariantImagePrimary(index, imgIndex)}
                              >
                                {isRtl ? 'رئيسي' : 'Primary'}
                              </button>
                              <button
                                type="button"
                                className="image-item-btn delete-btn"
                                onClick={() => removeVariantImage(index, imgIndex)}
                              >
                                {isRtl ? 'حذف' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Step 3: Occasions & Seasons (Perfume) or Product Images (Non-Perfume) */}
        {currentStep === 3 && isPerfumeProduct() && (
          <div className="form-section">
            <h3 className="step-title">{isRtl ? 'المناسبات والفصول' : 'Occasions & Seasons'}</h3>

            <div className="form-group mb-6">
              <label className="form-label">{isRtl ? 'المناسبات' : 'Occasions'}</label>
              <div className="checkbox-grid">
                {occasions.map((occasion) => (
                  <label
                    key={occasion.id}
                    className={`checkbox-item ${formData.occasionIds.includes(occasion.id) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.occasionIds.includes(occasion.id)}
                      onChange={() => toggleOccasion(occasion.id)}
                    />
                    <div className="checkbox-item-content">
                      <span className="checkbox-item-label">{isRtl ? occasion.nameAr : occasion.nameEn}</span>
                      {isRtl && <span className="checkbox-item-sub">{occasion.nameEn}</span>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{isRtl ? 'الفصول' : 'Seasons'}</label>
              <div className="checkbox-grid">
                {seasons.map((season) => (
                  <label
                    key={season.id}
                    className={`checkbox-item ${formData.seasonIds.includes(season.id) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.seasonIds.includes(season.id)}
                      onChange={() => toggleSeason(season.id)}
                    />
                    <div className="checkbox-item-content">
                      <span className="checkbox-item-label">{isRtl ? season.nameAr : season.nameEn}</span>
                      {isRtl && <span className="checkbox-item-sub">{season.nameEn}</span>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && !isPerfumeProduct() && (
          <div className="form-section">
            <h3 className="step-title">{isRtl ? 'صور المنتج' : 'Product Images'}</h3>

            <div className="form-group">
              <input
                type="file"
                multiple
                accept="image/*"
                ref={productImageInputRef}
                onChange={(e) => handleProductImageUpload(e.target.files)}
                hidden
              />
              <Button variant="secondary" type="button" onClick={() => productImageInputRef.current?.click()}>
                {isRtl ? 'رفع صور المنتج' : 'Upload Product Images'}
              </Button>

              {formData.productImages.length > 0 && (
                <div className="image-grid">
                  {formData.productImages.map((img, imgIndex) => (
                    <div key={img.id || imgIndex} className={`image-item ${img.isPrimary ? 'primary' : ''}`}>
                      <img src={img.dataUrl || img.url} alt={`Product Image ${imgIndex + 1}`} />
                      {img.isPrimary && (
                        <span className="image-item-badge">{isRtl ? 'رئيسي' : 'Primary'}</span>
                      )}
                      <div className="image-item-actions">
                        <button
                          type="button"
                          className="image-item-btn primary-btn"
                          onClick={() => setProductImagePrimary(imgIndex)}
                        >
                          {isRtl ? 'رئيسي' : 'Primary'}
                        </button>
                        <button
                          type="button"
                          className="image-item-btn delete-btn"
                          onClick={() => removeProductImage(imgIndex)}
                        >
                          {isRtl ? 'حذف' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Fragrance Notes (Perfume) or Review (Non-Perfume) */}
        {currentStep === 4 && isPerfumeProduct() && (
          <div className="form-section">
            <h3 className="step-title">{isRtl ? 'ملاحظات العطر' : 'Fragrance Notes'}</h3>

            {/* Top Notes */}
            <div className="form-group mb-6">
              <label className="form-label">{isRtl ? 'الملاحظات العلوية' : 'Top Notes'}</label>
              <Input
                value={topNoteSearch}
                onChange={(e) => setTopNoteSearch(e.target.value)}
                placeholder={isRtl ? 'ابحث عن ملاحظة علوية...' : 'Search top notes...'}
                dir={isRtl ? 'rtl' : 'ltr'}
                className="mb-2"
              />
              {topNoteSearch.trim().length > 0 && (
                <div className="note-suggestions" role="listbox">
                  {filterNotes(fragranceNotes, topNotes, topNoteSearch, 'top')
                    .slice(0, 12)
                    .map((note) => (
                      <button
                        key={note.id}
                        type="button"
                        className="note-suggestion-btn"
                        onClick={() => {
                          addFragranceNote(note.id, 1);
                          setTopNoteSearch('');
                        }}
                      >
                        {getNoteLabel(note)}
                      </button>
                    ))}
                  {filterNotes(fragranceNotes, topNotes, topNoteSearch, 'top').length === 0 && (
                    <div className="note-suggestion-empty">
                      {isRtl ? 'لا توجد نتائج' : 'No matches'}
                    </div>
                  )}
                </div>
              )}
              {topNotes.length > 0 && (
                <div className="tags-container">
                  {topNotes.map((note) => (
                    <span key={note.id} className="tag tag-top">
                      {isRtl ? note.nameAr : note.nameEn}
                      <button type="button" className="tag-remove" onClick={() => removeFragranceNote(note.id, 1)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Middle Notes */}
            <div className="form-group mb-6">
              <label className="form-label">{isRtl ? 'الملاحظات الوسطى' : 'Middle Notes'}</label>
              <Input
                value={middleNoteSearch}
                onChange={(e) => setMiddleNoteSearch(e.target.value)}
                placeholder={isRtl ? 'ابحث عن ملاحظة وسطى...' : 'Search middle notes...'}
                dir={isRtl ? 'rtl' : 'ltr'}
                className="mb-2"
              />
              {middleNoteSearch.trim().length > 0 && (
                <div className="note-suggestions" role="listbox">
                  {filterNotes(fragranceNotes, middleNotes, middleNoteSearch, 'middle')
                    .slice(0, 12)
                    .map((note) => (
                      <button
                        key={note.id}
                        type="button"
                        className="note-suggestion-btn"
                        onClick={() => {
                          addFragranceNote(note.id, 2);
                          setMiddleNoteSearch('');
                        }}
                      >
                        {getNoteLabel(note)}
                      </button>
                    ))}
                  {filterNotes(fragranceNotes, middleNotes, middleNoteSearch, 'middle').length === 0 && (
                    <div className="note-suggestion-empty">
                      {isRtl ? 'لا توجد نتائج' : 'No matches'}
                    </div>
                  )}
                </div>
              )}
              {middleNotes.length > 0 && (
                <div className="tags-container">
                  {middleNotes.map((note) => (
                    <span key={note.id} className="tag tag-middle">
                      {isRtl ? note.nameAr : note.nameEn}
                      <button type="button" className="tag-remove" onClick={() => removeFragranceNote(note.id, 2)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Base Notes */}
            <div className="form-group mb-6">
              <label className="form-label">{isRtl ? 'الملاحظات الأساسية' : 'Base Notes'}</label>
              <Input
                value={baseNoteSearch}
                onChange={(e) => setBaseNoteSearch(e.target.value)}
                placeholder={isRtl ? 'ابحث عن ملاحظة أساسية...' : 'Search base notes...'}
                dir={isRtl ? 'rtl' : 'ltr'}
                className="mb-2"
              />
              {baseNoteSearch.trim().length > 0 && (
                <div className="note-suggestions" role="listbox">
                  {filterNotes(fragranceNotes, baseNotes, baseNoteSearch, 'base')
                    .slice(0, 12)
                    .map((note) => (
                      <button
                        key={note.id}
                        type="button"
                        className="note-suggestion-btn"
                        onClick={() => {
                          addFragranceNote(note.id, 3);
                          setBaseNoteSearch('');
                        }}
                      >
                        {getNoteLabel(note)}
                      </button>
                    ))}
                  {filterNotes(fragranceNotes, baseNotes, baseNoteSearch, 'base').length === 0 && (
                    <div className="note-suggestion-empty">
                      {isRtl ? 'لا توجد نتائج' : 'No matches'}
                    </div>
                  )}
                </div>
              )}
              {baseNotes.length > 0 && (
                <div className="tags-container">
                  {baseNotes.map((note) => (
                    <span key={note.id} className="tag tag-base">
                      {isRtl ? note.nameAr : note.nameEn}
                      <button type="button" className="tag-remove" onClick={() => removeFragranceNote(note.id, 3)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Product Images for Perfume */}
            <div className="form-group">
              <label className="form-label">{isRtl ? 'صور المنتج' : 'Product Images'}</label>
              <input
                type="file"
                multiple
                accept="image/*"
                ref={productImageInputRef}
                onChange={(e) => handleProductImageUpload(e.target.files)}
                hidden
              />
              <Button variant="secondary" type="button" onClick={() => productImageInputRef.current?.click()}>
                {isRtl ? 'رفع صور المنتج' : 'Upload Product Images'}
              </Button>

              {formData.productImages.length > 0 && (
                <div className="image-grid">
                  {formData.productImages.map((img, imgIndex) => (
                    <div key={img.id || imgIndex} className={`image-item ${img.isPrimary ? 'primary' : ''}`}>
                      <img src={img.dataUrl || img.url} alt={`Product Image ${imgIndex + 1}`} />
                      {img.isPrimary && (
                        <span className="image-item-badge">{isRtl ? 'رئيسي' : 'Primary'}</span>
                      )}
                      <div className="image-item-actions">
                        <button
                          type="button"
                          className="image-item-btn primary-btn"
                          onClick={() => setProductImagePrimary(imgIndex)}
                        >
                          {isRtl ? 'رئيسي' : 'Primary'}
                        </button>
                        <button
                          type="button"
                          className="image-item-btn delete-btn"
                          onClick={() => removeProductImage(imgIndex)}
                        >
                          {isRtl ? 'حذف' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === 4 && !isPerfumeProduct() && (
          <div className="form-section">
            <h3 className="step-title">{isRtl ? 'مراجعة' : 'Review'}</h3>
            <div className="review-summary">
              <div className="review-item">
                <span className="review-label">{isRtl ? 'اسم المنتج' : 'Product Name'}</span>
                <span className="review-value">{formData.nameEn}</span>
              </div>
              <div className="review-item">
                <span className="review-label">{isRtl ? 'العلامة التجارية' : 'Brand'}</span>
                <span className="review-value">
                  {brands.find(b => b.id === formData.brandId)?.nameEn || '-'}
                </span>
              </div>
              <div className="review-item">
                <span className="review-label">{isRtl ? 'الفئة' : 'Category'}</span>
                <span className="review-value">
                  {categories.find(c => c.id === formData.categoryId)?.nameEn || '-'}
                </span>
              </div>
              <div className="review-item">
                <span className="review-label">{isRtl ? 'المتغيرات' : 'Variants'}</span>
                <span className="review-value">{formData.variants.length}</span>
              </div>
              <div className="review-item">
                <span className="review-label">{isRtl ? 'الصور' : 'Images'}</span>
                <span className="review-value">{formData.productImages.length}</span>
              </div>
            </div>
            <p className="text-muted text-center mt-4">
              {isRtl ? 'المنتج جاهز للحفظ' : 'Product is ready to be saved'}
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default ProductModal;
